import type { ShippingLabelViewModel } from "@/modules/chat-tools/domain/build-shipping-label";
import { LuckPrinterBleTransport } from "./ble-transport";
import type { LuckContentMode } from "./content-modes";
import type { LuckPaperMode } from "./print-modes";
import { printShippingLabelViaBle } from "./print-label";
import type { LuckPrinterDensity } from "./protocol";
import {
  getSavedLuckPrinterContentMode,
  getSavedLuckPrinterDensity,
  getSavedLuckPrinterPaperMode,
} from "./storage";

export type BatchLabelItem = {
  orderId: string;
  orderNumber: string;
  label: ShippingLabelViewModel;
};

export type BatchPrintProgress = {
  current: number;
  total: number;
  orderNumber: string;
  message: string;
};

export type BatchPrintFailure = {
  orderId: string;
  orderNumber: string;
  error: Error;
};

export type BatchPrintResult = {
  printed: number;
  failures: BatchPrintFailure[];
};

export type PrintShippingLabelsBatchOptions = {
  items: BatchLabelItem[];
  density?: LuckPrinterDensity;
  contentMode?: LuckContentMode;
  paperMode?: LuckPaperMode;
  transport?: LuckPrinterBleTransport;
  onProgress?: (progress: BatchPrintProgress) => void;
  stopOnError?: boolean;
};

export async function printShippingLabelsBatch(
  options: PrintShippingLabelsBatchOptions,
): Promise<BatchPrintResult> {
  if (options.items.length === 0) {
    return { printed: 0, failures: [] };
  }

  const transport = options.transport ?? new LuckPrinterBleTransport();
  const ownsTransport = !options.transport;
  const density = options.density ?? getSavedLuckPrinterDensity();
  const contentMode = options.contentMode ?? getSavedLuckPrinterContentMode();
  const paperMode = options.paperMode ?? getSavedLuckPrinterPaperMode();
  const total = options.items.length;
  const failures: BatchPrintFailure[] = [];
  let printed = 0;

  try {
    if (!transport.connectedDevice) {
      options.onProgress?.({
        current: 0,
        total,
        orderNumber: "",
        message: "ກຳລັງເຊື່ອມຕໍ່...",
      });
      await transport.connect();
    }

    for (let index = 0; index < options.items.length; index++) {
      const item = options.items[index];
      if (!item) continue;

      options.onProgress?.({
        current: index + 1,
        total,
        orderNumber: item.orderNumber,
        message: `ກຳລັງພິມ ${item.orderNumber} (${index + 1}/${total})`,
      });

      try {
        await printShippingLabelViaBle({
          label: item.label,
          density,
          contentMode,
          paperMode,
          transport,
          onProgress: (message) => {
            options.onProgress?.({
              current: index + 1,
              total,
              orderNumber: item.orderNumber,
              message,
            });
          },
        });
        printed += 1;
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        failures.push({
          orderId: item.orderId,
          orderNumber: item.orderNumber,
          error: err,
        });
        if (options.stopOnError) break;
      }
    }
  } finally {
    if (ownsTransport) {
      await transport.disconnect();
    }
  }

  return { printed, failures };
}
